<template>
  <!-- "One of my domains": a subdomain of a domain in their account, with its DNS
       record set for them where we can — never replacing one without a yes. -->
  <div class="kvm-own">
    <v-autocomplete v-model="domainName" :items="domainItems" :loading="loading" label="Your domain" outlined dense
                    persistent-hint class="mb-2" :no-data-text="loading ? 'Loading your domains…' : 'No domains found in your account'"
                    hint="A domain in one of your hosting accounts, or registered with us" />
    <template v-if="entry">
      <v-text-field v-model.trim="sub" label="Subdomain" outlined dense persistent-hint class="mb-2"
                    :suffix="'.' + entry.domain" :error-messages="problem ? [problem] : []"
                    :hint="entry.inHosting ? 'Your website keeps ' + entry.domain + ' and www.' + entry.domain + '.' : 'Leave blank to use ' + entry.domain + ' itself.'"
                    @input="autoSub = false" />

      <div v-if="checking" class="kvm-own__status kvm-help text-caption mb-2" role="status">
        <v-progress-circular indeterminate size="14" width="2" color="primary" class="mr-2" aria-hidden="true" />Checking {{ hostname }}…
      </div>
      <template v-else-if="route && !problem">
        <v-alert v-if="conflicts.length" type="warning" dense text class="kvm-own__alert mb-2" role="alert">
          <strong>{{ hostname }}</strong> already points to
          {{ conflicts.map(c => c.address + (c.type !== 'A' ? ` (${c.type})` : '')).join(', ') }}.
          Using it for this app replaces that — whatever it reaches now stops working at this address.
          <v-checkbox v-model="confirmed" dense hide-details class="mt-1" :label="'Replace it — point ' + hostname + ' to this app'" />
        </v-alert>
        <p v-else-if="route.kind !== 'manual'" class="kvm-help text-caption mb-2">
          We'll set {{ hostname }} up in {{ route.kind === 'cpanel' ? 'your hosting account’s DNS' : 'your domain’s DNS' }} for you.
        </p>
        <p v-else class="kvm-help text-caption mb-2">
          We can't change DNS for {{ entry.domain }} from here — point an A record for {{ hostname }} to
          <strong>{{ serverIp || 'your server’s IP address' }}</strong> (HTTPS starts working once it does).
        </p>
      </template>
      <p v-if="error" class="error--text text-caption mb-2" role="alert">{{ error }}</p>
    </template>
  </div>
</template>

<script>
import { loadOwnDomains, subdomainProblem, hostnameFor, dnsRoute, currentRecords, conflictsWith } from '@/helpers/ownDomains'

const CHECK_DELAY_MS = 400

// An app name as a subdomain label (app names may have "_", hostnames can't).
const asSubdomain = name => String(name || '').toLowerCase().replace(/_/g, '-')
  .replace(/[^a-z0-9-]/g, '').replace(/^-+|-+$/g, '').slice(0, 63).replace(/-+$/, '')

/**
 * v-model: { entry, sub, hostname, route, conflicts, confirmed, ready } — `ready`
 * once a domain and subdomain are chosen, checked, and anything it would replace
 * was agreed to. Apply it with helpers/ownDomains pointRecord(route, sub, ip).
 */
export default {
  name: 'KvmOwnDomain',
  props: {
    value: { type: Object, default: null },
    // The subdomain starts as the app's name (until the customer changes it).
    appName: { type: String, default: '' },
    // The server's IP: a record already pointing there isn't a conflict. Null
    // when the server isn't known yet.
    serverIp: { type: String, default: null },
  },
  data () {
    return {
      domains: [],
      loading: false,
      domainName: (this.value && this.value.entry && this.value.entry.domain) || null,
      sub: this.value && this.value.sub != null ? this.value.sub : asSubdomain(this.appName),
      autoSub: !(this.value && this.value.entry),
      checking: false,
      route: null,
      conflicts: [],
      confirmed: !!(this.value && this.value.confirmed),
      error: '',
      timer: null,
      routes: {}, // domain → its DNS route (asked once)
    }
  },
  computed: {
    // The domains registered with us (the session's service list).
    registered () {
      return (this.$store && this.$store.getters.domains) || []
    },
    domainItems () {
      return this.domains.map(d => ({ text: d.domain + (d.inHosting ? ' — hosting' : ''), value: d.domain }))
    },
    entry () {
      return this.domains.find(d => d.domain === this.domainName) || null
    },
    problem () {
      return this.entry ? subdomainProblem(this.entry, this.sub) : null
    },
    hostname () {
      return this.entry ? hostnameFor(this.entry, this.sub) : ''
    },
    ready () {
      return !!(this.entry && !this.problem && !this.checking && this.route && !this.error &&
        (!this.conflicts.length || this.confirmed))
    },
  },
  watch: {
    appName (name) { if (this.autoSub) this.sub = asSubdomain(name) },
    domainName () { this.schedule() },
    sub () { this.schedule() },
    serverIp () { this.schedule() },
    confirmed () { this.emit() },
  },
  async mounted () {
    this.loading = true
    try {
      this.domains = await loadOwnDomains(this.registered)
    } finally {
      this.loading = false
    }
    if (this.entry) this.schedule()
  },
  beforeDestroy () {
    clearTimeout(this.timer)
  },
  methods: {
    schedule () {
      clearTimeout(this.timer)
      this.route = null
      this.conflicts = []
      this.confirmed = false
      this.error = ''
      this.emit()
      if (!this.entry || this.problem) return
      this.checking = true
      this.timer = setTimeout(this.check, CHECK_DELAY_MS)
    },
    // Where its DNS is managed, and what the name points at today.
    async check () {
      const entry = this.entry
      const sub = this.sub
      try {
        const route = this.routes[entry.domain] || (this.routes[entry.domain] = await dnsRoute(entry))
        const records = await currentRecords(route, sub)
        if (entry !== this.entry || sub !== this.sub) return // changed meanwhile
        this.route = route
        this.conflicts = conflictsWith(records, this.serverIp)
      } catch (e) {
        if (entry === this.entry && sub === this.sub) this.error = `Couldn't check ${hostnameFor(entry, sub)}: ${e.message}`
      } finally {
        if (entry === this.entry && sub === this.sub) this.checking = false
        this.emit()
      }
    },
    emit () {
      this.$emit('input', {
        entry: this.entry,
        sub: this.sub,
        hostname: this.hostname,
        route: this.route,
        conflicts: this.conflicts,
        confirmed: this.confirmed,
        ready: this.ready,
        // Why it isn't ready yet, for the form's own message.
        notReady: this.ready ? null : this.notReadyReason(),
      })
    },
    notReadyReason () {
      if (!this.entry) return 'Choose which of your domains to use.'
      if (this.problem) return this.problem
      if (this.error) return this.error
      if (this.checking || !this.route) return 'Still checking that address — one moment.'
      return `Confirm replacing what ${this.hostname} points to now, or choose another subdomain.`
    },
  },
}
</script>

<style scoped lang="scss">
@import './kvm-ui';

.kvm-help { @include kvm-secondary-text; }
.kvm-own__status { display: flex; align-items: center; }
.kvm-own__alert ::v-deep .v-alert__content { color: #212121 !important; }
.theme--dark .kvm-own__alert ::v-deep .v-alert__content { color: #eceff1 !important; }
</style>
